import structuredClone from "@ungap/structured-clone";

type Data = Array<any> | { [key: string]: any };

// This function deletes properties from a given object in place and recursively
// on nested objects and arrays. It also avoids circular references.
// @param {Data} data - The object to be modified.
// @param {string[] | string} filters - The property/properties to be deleted from the object.
// @param {WeakSet} seen - Keeps track of objects already seen to avoid circular references.
// @param {number} pageReferencesCount - Keeps track of the amount of page references per object.
export const deepDeleteInPlace = (
    data: Data,
    filters: string[],
    seen = new WeakSet(),
    pageReferencesCount: number = 0
) => {
    if (seen.has(data)) {
        // Skip circular reference
        return;
    }

    seen.add(data);

    // Recursively processes the object properties.
    const processElement = (element) => {
        // If it is not null, not a date and it is an array or an object, calls deepDeleteInPlace recursively.
        if (
            element != null &&
            !(element instanceof Date) &&
            (Array.isArray(element) || typeof element === "object")
        ) {
            if (
                element?.sys?.contentType?.sys?.type === "Link" &&
                element?.sys?.contentType?.sys?.id === "pagePath"
            ) {
                pageReferencesCount += 1;
            }

            // We want to keep the root page and delete the rest of page references
            // that could exist inside modules/parts since what we actually
            // need in those cases is just the completeSlug field.
            if (pageReferencesCount > 1 && !filters.includes("page")) {
                filters.push("page");
            }

            deepDeleteInPlace(element, filters, seen, pageReferencesCount);
        }
    };

    if (Array.isArray(data)) {
        // If the data is an array, applies processElement to each of its elements.
        data.forEach((el) => processElement(el));
        return;
    }

    // Loops through the object properties and deletes the ones specified by the filter/s.
    for (const key in data) {
        if (filters.includes(key)) {
            delete data[key];
            continue;
        }

        const value = data[key];

        // Calls processElement to keep searching for nested properties to delete.
        processElement(value);
    }

    seen.delete(data);
    // Remove page from filters when the recursion ends to avoid deleting
    // root page references in other properties of the main object.
    filters.forEach((filter) => {
        if (filter === "page") {
            filters.splice(filters.indexOf(filter), 1);
        }
    });
};

export const deepDelete = (data: Data, filters: string[]) => {
    const clonedData = structuredClone(data);

    deepDeleteInPlace(clonedData, filters);

    return clonedData;
};
